// Frontend data contracts for the Disposition Framework screen (4a), transcribed from the
// engine spec §8 (RuleSet, BoxDefinition, DispositionRecord). The screen is a VIEW of these
// shapes — every rendered value re-derives from a record + its ruleset (handoff §7 invariant 3).

export type BoxId = 'box1' | 'box2' | 'box3' | 'box4';
export type Outcome = BoxId | 'decommission' | 'consolidate' | 'queue';
export type Timing = 'cat1' | 'cat2' | 'cat3';

// Materiality is a 4-band classification, not a $ figure, with an optional $ override (Ryan 2026-07-21).
export type Band = 'Critical' | 'High' | 'Moderate' | 'Low';
export const BAND_RANK: Record<Band, number> = { Low: 1, Moderate: 2, High: 3, Critical: 4 };
export type RecordStatus = 'auto_confirmed' | 'committed' | 'human_review' | 'provisional' | 'deferred' | 'regenerate';

// Turn 5 · 5a — per-gate evaluation the provisional worked example projects (§1.3, §1.5).
export type GateStatus = 'pass' | 'clear' | 'blocking' | 'unresolved' | 'contingent' | 'deferred';
export interface GateEvaluation {
  id: string;                 // G1..G8
  status: GateStatus;
  needs_hint?: string | null;    // "Markets COO to name a replacement owner"
  needs_owner?: string | null;   // "Markets COO"
  needs_eta?: string | null;     // "2 weeks"
  contingent_on?: string[] | null;
}
// One of the two contingent outcomes the engine computes by running §5 twice (§1.4.2).
export interface ContingentBranch {
  condition: string;          // "IF Murex owns it"
  disposition: Outcome;       // the box/outcome it would route to
  probability: number;        // 0..1
  justification: string;
}

// §8.4
export interface BoxDefinition {
  id: BoxId;
  label: string;
  residual_risk: 'HIGH' | 'MEDIUM' | 'LOW';
  target_platform_catalog: string[];
  downstream_path: string;
}

// §8.1 (the subset the screen reads)
export interface RuleSet {
  version: number;
  published_at?: string;
  boxes: BoxDefinition[];
}

export interface GateFired {
  id: string;        // G1..G8
  action: string;    // e.g. "FORBID box4"
}

// Item 3 ("EUC-to-artifact is one-to-many, and uploads can create inventory rows"): one attached,
// already-decomposed run. A row's disposition is now scored over the UNION of these, not one file.
export interface EucArtifact {
  run_id: string;
  file: string | null;
  added_at: string;
}

// §8.5 — the committed decision the screen renders.
export interface DispositionRecord {
  euc_id: string;
  file: string;
  dept: string;
  // Where this row came from: the original 154-row Apparity spreadsheet import, or a row created
  // automatically the first time an upload didn't match anything in that population. Absent on
  // older cached records — treat missing as 'apparity_import' (the pre-item-3 default).
  origin?: 'apparity_import' | 'discovered_on_upload';
  // The decomposed runs grouped under this EUC (1..N). A consultant attaches/detaches/regroups
  // these; the disposition scorecard is derived from their combined extracted logic.
  artifacts?: EucArtifact[];
  // Item 12 (owner attestation) — the CxO Owner name, when the inventory carries one. Used to
  // group EUCs for the per-owner attestation export pack; null for rows with no named owner (G1's
  // "no owner" case) or for records built before this field existed.
  owner?: string | null;
  ruleset_version: number;
  disposition: Outcome;
  timing: Timing;
  fast_follow?: Timing | null;
  confidence: number | null;
  status: RecordStatus;
  gates_fired: GateFired[];
  factor_vector: Record<string, number>;
  box_shares: Partial<Record<BoxId, number>>;
  reasoning_summary: string;
  downstream_path?: string | null;
  materiality_band?: Band | null;   // consultant 4-band (Risk Tier + Reg Submission + Data Sensitivity)
  materiality_usd?: number | null;  // optional $ override — dollars win over the band when present
  source_type: string;              // 'xlsm' | 'xlsx' | 'yxmd' | 'dsx' | 'xml' ...
  // Provisional (Cat 3) records lead with what must clear before they can commit.
  resolution?: { blocker: 'owner_unknown' | 'dependency_unresolved' | 'strategic_decision'; owner?: string; target_date?: string } | null;
  // Turn 5 · 5a additions (§1.5). Present on provisional/deferred records.
  provisional_reason?: string | null;      // one-line summary for the header
  blockers?: string[];                     // gate ids currently blocking
  gate_evaluations?: GateEvaluation[];      // per-gate status for the six step rows
  contingent_branches?: ContingentBranch[]; // the two possible outcomes
  // Turn 5 · 5b additions (§2.10). Cluster membership + retirement provenance.
  cluster_id?: string | null;
  is_survivor?: boolean;
  retirement_reason?: 'dead' | 'superseded' | 'zero_consumers' | string | null;  // 'consolidated_into:{id}'
  // Item 7 (Asana 1216373781992284): which platform THIS EUC routes to, server-resolved and merged
  // onto the record by GET /api/framework/dispositions (routes/framework.js resolveTargetPlatform) —
  // an explicit per-EUC pick, else the box's tenant default, else the ruleset's seeded default.
  // null for outcomes with no box (decommission / consolidate / queue, or a still-provisional record).
  selected_target_platform?: string | null;
  // Item 10 (spec §7.3) — a consultant's per-EUC override, applied on read by GET /dispositions.
  // `disposition` above already reflects the override; `engine_disposition` preserves what the
  // engine itself said so the override is visibly an override, never a silent edit.
  engine_disposition?: Outcome;
  disposition_override?: {
    disposition: Outcome;
    reason: string;
    actor: { id: number | string; name: string | null; email: string | null; role?: string | null };
    at: string;
    original_disposition: Outcome | null;
  } | null;
  // Item 11 (spec §10.3, §11) — set once a consultant approves or overrides + commits this EUC via
  // POST /api/framework/commit, GET /dispositions merges it on read (frameworkStore.js's commit_log,
  // same merge-on-read pattern as disposition_override above). `routing` is the actual downstream
  // path(s) the engine committed to, computed server-side by commitRouting.js — never re-derived here.
  committed?: boolean;
  committed_by?: { id: number | string; name: string | null; email: string | null; role?: string | null } | null;
  committed_at?: string | null;
  commit_decision?: 'approved' | 'overridden' | null;
  routing?: RoutingStep[] | null;
}

// commitRouting.js's return shape — one row per generation chain a committed disposition feeds
// (box3 can fan out to Path A + Path C + Path B; every other disposition is a single-element array).
export interface RoutingStep {
  path: 'A' | 'B' | 'C' | null;   // null only for box4's standalone doc bundle (no generation chain)
  profile: string;                 // 'full' | 'light' | 'platform_config' | 'cloud_decision' | 'sunset' | 'doc_bundle'
  note: string;
}

// Turn 5 · 5b — a candidate-merge cluster (§2.10). Survivor takes the box; the rest retire.
export type ClusterStatus = 'pending_review' | 'confirmed' | 'contested' | 'archived';
export interface ClusterRecord {
  id: string;                 // "C-01"
  status: ClusterStatus;
  confidence: number;         // 0..1
  member_ids: string[];
  survivor_id: string | null;
  survivor_box?: BoxId | null;
  survivor_selection_rationale: string;
  signals: {
    shared_input_feeds: string[];
    formula_fingerprint_overlap: number | null;    // 0..1; null = needs file extraction (not in the inventory)
    output_schema_overlap_columns: number | null;  // null = needs file extraction (not in the inventory)
    owner_overlap_ratio: string;            // "3/4"
  };
  dispute?: { parties: string[]; question: string; blocks_count: number } | null;
  estimated_monthly_hours_saved: number;
  // 'placeholder' when the inventory carried no hours column, so the tile can say "estimated"
  // instead of presenting a default as a measurement.
  estimated_monthly_hours_basis?: 'inventory' | 'placeholder';
  ruleset_version: number;
  // Set when a consultant overrode the rule-selected survivor. The reason is required (spec §5).
  survivor_override?: { survivor_id: string; reason: string; at: string } | null;
}

// A pair the algorithm scored just UNDER overlap_epsilon. Spec §6: these surface for review rather
// than being silently decided as unrelated — the algorithm is saying it is not sure, not saying no.
export interface NearMissPair {
  a_id: string;
  b_id: string;
  score: number;
  overlaps: {
    input: number | null;
    formula: number | null;
    output: number | null;
  };
  reason: string;
}

// The six-question projection (handoff §3). Cards 1–4 are the WHERE axis; 5–6 the WHEN axis.
export interface QuestionDef {
  n: number;
  axis: 'where' | 'when';
  question: string;
  explain: string;
  gates: string;   // the engine primitives this question projects, for auditor traceability
  refs?: string[]; // gate/factor ids for the drill-down chips (Edit 6), e.g. ['G1','G2','E3']
}

// A gate or factor definition, mirrored from the engine ruleset for the six-question drill-down.
export interface PrimitiveDef { kind: 'gate' | 'factor'; name: string; detail: string; }
